import { OrgId } from '@business/entities/organization';
import { PageQuery } from '../common';
import { ProposalId, ProposalStatus } from './ProposalResponse';

/**
 * Raw proposal_histories `type` values stored by the backend
 * (see Proposal::addHistory usages).
 */
export type ProposalActivityType = 'created' | 'sent' | 're-sent' | 'viewed' | 'signed' | 'deposited' | 'revised' | 'updated' | 'archived' | 'note';

export interface ProposalActivityRowResponse {
  id: string;
  type: ProposalActivityType;
  message: string;
  body?: string;
  createdAt: number;
  proposal?: {
    id: ProposalId;
    title: string;
    amount: number;
    status: ProposalStatus;
    image: string;
  };
  organization?: {
    id: OrgId;
    name: string;
  };
}

export interface ProposalActivitiesPageQuery extends PageQuery {
  orgId?: OrgId;
  search?: string;
  /** Comma-separated list of raw ProposalActivityType values. */
  types?: string;
  startDate?: string;
  endDate?: string;
}

export interface ProposalActivitiesStats {
  requestId?: string;
  /** Count keyed by raw activity type. */
  counts: Partial<Record<ProposalActivityType, number>>;
  totalValue: number;
  totalActivities: number;
  totalProposals: number;
}

/**
 * Business-friendly groupings used by the filter dropdown and the stat
 * tiles. Each group maps to one or more raw `type` values so the user can
 * think in outcomes ("Accepted") rather than DB enums ("signed").
 */
export interface ProposalActivityGroup {
  key: string;
  label: string;
  types: ProposalActivityType[];
  icon: string;
  /** reactstrap/bootstrap contextual color. */
  color: string;
}

export const ProposalActivityGroups: ProposalActivityGroup[] = [
  { key: 'created', label: 'Created', types: ['created'], icon: 'bx bx-plus-circle', color: 'secondary' },
  { key: 'sent', label: 'Sent', types: ['sent', 're-sent'], icon: 'bx bx-send', color: 'primary' },
  { key: 'viewed', label: 'Viewed', types: ['viewed'], icon: 'mdi mdi-eye', color: 'info' },
  { key: 'accepted', label: 'Accepted', types: ['signed'], icon: 'bx bx-check-circle', color: 'success' },
  { key: 'deposited', label: 'Deposited', types: ['deposited'], icon: 'bx bx-dollar-circle', color: 'success' },
  { key: 'edited', label: 'Edited', types: ['revised', 'updated'], icon: 'bx bx-edit', color: 'warning' },
  { key: 'archived', label: 'Archived', types: ['archived'], icon: 'bx bx-archive', color: 'danger' },
];

const TYPE_TO_GROUP: Record<string, ProposalActivityGroup> = ProposalActivityGroups.reduce((acc, group) => {
  group.types.forEach(type => (acc[type] = group));
  return acc;
}, {} as Record<string, ProposalActivityGroup>);

export function getProposalActivityGroup(type: ProposalActivityType): ProposalActivityGroup {
  return TYPE_TO_GROUP[type] ?? { key: type, label: type, types: [type], icon: 'bx bx-bolt', color: 'secondary' };
}
